import { useState } from 'react';
import { formatCurrencyInput, extractNumericInput } from '../../utils/numberFormat';
import { AssetAccount, AccountType, Goal, ACCOUNT_TYPE_LABELS } from '../../types/api.types';
import { useAssets } from '../../hooks/useApi';
import styles from './AssetDetailModal.module.scss';

const ACCOUNT_TAX_STATUS: Record<AccountType, string> = {
    NRE: 'Interest is tax-free in India (no TDS) and fully repatriable. However, it is taxable as foreign income in the US and Canada — report on FBAR/FATCA if account balances exceed thresholds.',
    NRO: 'Interest attracts 31.2% TDS in India (30% + 4% cess), withheld automatically by your bank. Repatriation capped at USD 1M/year after tax. Claim TDS as a foreign tax credit on US Form 1116 or Canadian T2209 under DTAA.',
    PPF: 'Exempt-Exempt-Exempt (EEE) in India — contributions, growth, and maturity proceeds are all tax-free. The IRS and CRA do not recognise PPF as tax-advantaged; growth may be reportable and taxable annually in the US/Canada.',
    NPS: 'Contributions are deductible under Sec 80CCD in India. At maturity, 60% is tax-free; the remaining 40% must purchase an annuity (taxable as income). Treated as a foreign pension for US/Canada purposes.',
    EQUITY_IN:
        'LTCG above ₹1.25L/year taxed at 12.5% in India; STCG at 20%. US/Canadian residents report gains on FBAR/FATCA. DTAA credits apply for Indian tax paid to avoid double taxation.',
    MF_IN: 'Likely classified as a Passive Foreign Investment Company (PFIC) for US persons — subject to punitive excess-distribution tax unless a QEF election is filed (Form 8621). LTCG taxed at 12.5% above ₹1.25L in India. Canadian residents report as foreign property above C$100k.',
    REAL_ESTATE:
        'Rental income subject to 30% TDS (NRI rate). LTCG on sale taxed at 20% with indexation (or 12.5% without). Repatriation requires Form 15CA/15CB from a CA and is capped at USD 1M/year from NRO account.',
    '401K': 'US tax-deferred — contributions reduce taxable income; growth and distributions taxed as ordinary federal income. RMDs begin at age 73. India-US DTAA Article 20 generally allows the US exclusive taxing rights on distributions.',
    IRA_ROTH:
        'Qualified distributions are completely tax-free in the US. Canada recognises Roth IRA as tax-exempt under the Canada-US Tax Treaty (Article XVIII). Indian tax treatment at withdrawal depends on residency status and applicable DTAA.',
    IRA_TRADITIONAL:
        'Tax-deferred growth; distributions taxed as ordinary US federal income. RMDs required at age 73. Treated similarly to a 401k under DTAA provisions for cross-border situations.',
    TAXABLE_US:
        'No contribution limits or mandatory withdrawal rules. Dividends and capital gains taxed annually. Long-term capital gains rate: 0%, 15%, or 20% depending on income. Report as foreign financial account if held while a non-US resident.',
    REAL_ESTATE_US:
        'Rental income taxed at ordinary federal rates. Long-term capital gains qualify for 0%/15%/20% rates. Principal residence exclusion up to $250k ($500k joint). Non-resident sellers subject to FIRPTA withholding on gross proceeds.',
    VEHICLE_US:
        'Depreciating asset — included for net-worth tracking only. No investment return expected; no special tax treatment.',
    TFSA: 'Completely tax-free in Canada — contributions, growth, and withdrawals all exempt from Canadian tax and no impact on government benefits. The IRS does not recognise TFSA as tax-advantaged; US persons must report TFSA income annually.',
    RRSP: 'Tax-deferred in Canada — contributions reduce taxable income; withdrawals taxed as ordinary income. Must convert to RRIF by age 71. The US recognises RRSP as tax-deferred under the Canada-US Tax Treaty (Article XVIII(7)) — no annual US tax on growth.',
    TAXABLE_CAN:
        'Capital gains benefit from a 50% inclusion rate in Canada — only half the gain is added to income. Eligible Canadian dividends receive a dividend tax credit. Foreign income (e.g. US dividends) is taxed at the full marginal rate.',
    REAL_ESTATE_CAN:
        'Principal residence is exempt from capital gains in Canada. Investment properties face 50% capital gains inclusion on sale. Leaving Canada permanently triggers a deemed disposition at fair market value (departure tax); RRSP and TFSA are exempt.',
    VEHICLE_CAN:
        'Depreciating asset — included for net-worth tracking only. No investment return expected; no special tax treatment.',
};

interface AssetDetailModalProps {
    asset: AssetAccount;
    goals: Goal[];
    onClose: () => void;
    onSaved: (updated: AssetAccount) => void;
}

export function AssetDetailModal({ asset, goals, onClose, onSaved }: AssetDetailModalProps) {
    const { updateAsset } = useAssets();
    const [balance, setBalance] = useState(String(asset.balance));
    const [expectedReturn, setExpectedReturn] = useState(String(asset.expectedReturn));
    const [ownership, setOwnership] = useState<'SELF' | 'SPOUSE' | 'JOINT'>(asset.ownership);
    const [linkedGoalId, setLinkedGoalId] = useState<string>(asset.linkedGoalId ?? '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const taxBlurb = ACCOUNT_TAX_STATUS[asset.accountType];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const patch = {
                balance: Number(balance),
                expectedReturn: Number(expectedReturn),
                ownership,
                linkedGoalId: linkedGoalId || undefined,
            };
            await updateAsset(asset.accountId, patch);
            onSaved({ ...asset, ...patch });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.header}>
                    <div className={styles.headerMeta}>
                        <h2 className={styles.assetLabel}>{asset.label}</h2>
                        <span className={styles.accountType}>{ACCOUNT_TYPE_LABELS[asset.accountType]}</span>
                    </div>
                    <button className={styles.closeButton} onClick={onClose} aria-label="Close">
                        &times;
                    </button>
                </div>

                {taxBlurb && (
                    <div className={styles.taxBlurb}>
                        <span className={styles.taxBlurbIcon}>🏦</span>
                        <p>{taxBlurb}</p>
                    </div>
                )}

                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.row}>
                        <div className={styles.field}>
                            <label className={styles.label}>Balance ({asset.currency})</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                className={styles.input}
                                value={formatCurrencyInput(balance, asset.currency)}
                                onChange={(e) => setBalance(extractNumericInput(e.target.value))}
                                required
                            />
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label}>Expected Return (%)</label>
                            <input
                                type="number"
                                className={styles.input}
                                value={expectedReturn}
                                onChange={(e) => setExpectedReturn(e.target.value)}
                                min={-20}
                                max={50}
                                step="0.1"
                                required
                            />
                        </div>
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Ownership</label>
                        <div className={styles.radioGroup}>
                            {(['SELF', 'SPOUSE', 'JOINT'] as const).map((o) => (
                                <label key={o} className={styles.radioLabel}>
                                    <input
                                        type="radio"
                                        name="ownership"
                                        value={o}
                                        checked={ownership === o}
                                        onChange={() => setOwnership(o)}
                                    />
                                    {o.charAt(0) + o.slice(1).toLowerCase()}
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Linked Goal (optional)</label>
                        <select
                            className={styles.input}
                            value={linkedGoalId}
                            onChange={(e) => setLinkedGoalId(e.target.value)}
                        >
                            <option value="">— None —</option>
                            {goals.map((g) => (
                                <option key={g.goalId} value={g.goalId}>
                                    {g.label}
                                </option>
                            ))}
                        </select>
                        {linkedGoalId && goals.find((g) => g.goalId === linkedGoalId) && (
                            <p className={styles.goalHint}>
                                This asset is earmarked for{' '}
                                <strong>{goals.find((g) => g.goalId === linkedGoalId)!.label}</strong>.
                            </p>
                        )}
                    </div>

                    {error && <p className={styles.error}>{error}</p>}

                    <div className={styles.actions}>
                        <button type="button" className={styles.cancelButton} onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className={styles.submitButton} disabled={saving}>
                            {saving ? 'Saving…' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
